import { create } from "zustand";
import { registerAuthBinding, type Portal } from "@/lib/apiClient";

// Refresh this many ms before the access token's actual expiry.
const DEFAULT_REFRESH_MARGIN_MS = 60_000;
// Last-resort lifetime when neither the refresh response nor the JWT itself
// carries a usable expiry — without this, an invalid/NaN value would collapse the
// proactive-refresh delay to ~0ms and the timer would fire in a tight loop
// instead of once per token lifetime.
const FALLBACK_EXPIRES_IN_MINUTES = 15;

/**
 * Reads the `exp` claim (seconds since the epoch) out of a JWT and returns how
 * many minutes from now it expires. Returns NaN for anything it can't read — a
 * malformed token, a missing or non-numeric `exp`, a non-JWT string — so callers
 * fall through to their next expiry source. This is only a client-side timing
 * hint for when to refresh proactively; the token's real validity is always
 * enforced server-side.
 *
 * The patient portal's /signin and /refresh send no expiry field at all, but the
 * access token they return always carries its own `exp`, so this recovers an
 * accurate refresh schedule instead of leaning on the blind 15m fallback.
 */
function readJwtExpiryMinutes(token: string | null | undefined): number {
  const payload = token?.split(".")[1];
  if (!payload) return NaN;
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { exp?: unknown };
    if (typeof claims.exp !== "number") return NaN;
    return (claims.exp * 1000 - Date.now()) / 60_000;
  } catch {
    return NaN;
  }
}

export interface AuthSession<TUser> {
  accessToken: string;
  /**
   * Access token lifetime in MINUTES, matching the backend's `expires_in_minutes`
   * convention (the admin portal sends this field; the patient portal currently
   * sends no expiry at all — pass NaN/undefined in that case to use the fallback).
   */
  expiresIn: number;
  user: TUser;
}

export interface AuthApiResult<T> {
  data: T | null;
  error: string | null;
  code?: string | null;
  retryAfterSeconds?: number;
  /** Present only on EMAIL_NOT_VERIFIED-style errors referencing an existing pending OTP. */
  otpExpiresInSeconds?: number;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  code?: string;
  retryAfterSeconds?: number;
  otpExpiresInSeconds?: number;
}

export interface AuthStoreConfig<TUser, TCredentials extends unknown[]> {
  portal: Portal;
  signIn: (...credentials: TCredentials) => Promise<AuthApiResult<AuthSession<TUser>>>;
  /** Reads the httpOnly refresh cookie server-side; no args needed. */
  refresh: () => Promise<AuthApiResult<AuthSession<TUser>>>;
  signOut: () => Promise<void>;
  refreshMarginMs?: number;
}

export interface AuthState<TUser> {
  accessToken: string | null;
  user: TUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface AuthActions<TUser, TCredentials extends unknown[]> {
  setSession: (data: AuthSession<TUser>) => void;
  clearSession: () => void;
  signIn: (...credentials: TCredentials) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  silentRestore: () => Promise<void>;
}

export type AuthStore<TUser, TCredentials extends unknown[]> = AuthState<TUser> & AuthActions<TUser, TCredentials>;

/**
 * Builds one portal's auth store. The access token lives only in this in-memory
 * Zustand state — it is never written to localStorage/sessionStorage. Session
 * persistence across reloads comes from the backend's httpOnly refresh cookie via
 * `silentRestore`, not from anything readable by frontend JS.
 */
export function createAuthStore<TUser, TCredentials extends unknown[]>(
  config: AuthStoreConfig<TUser, TCredentials>,
) {
  let refreshTimer: ReturnType<typeof setTimeout> | undefined;
  // Dedupes concurrent refresh attempts (proactive timer + N simultaneous 401s) into a
  // single in-flight request, so a burst of protected calls can't fire N parallel
  // refresh calls and race each other against a single-use/rotated refresh token.
  let refreshPromise: Promise<boolean> | null = null;
  // Bumped on every setSession/clearSession. Captured by refreshSilently before it awaits
  // the network call; if the generation has moved on by the time it resolves (a manual
  // sign-out, or another refresh, happened in the meantime), its result is stale and must
  // not be applied — otherwise a refresh that was already in flight when the user clicked
  // "logout" can resolve afterwards and silently re-establish the session.
  let generation = 0;
  // True from the moment signOut() starts clearing local state until the server has
  // actually revoked the refresh cookie (that network call is slow — ~5s observed — and
  // callers navigate away without awaiting signOut() at all). The generation bump above
  // only protects refreshes that were already in flight before logout; it does nothing
  // for a BRAND NEW refresh attempt that starts after logout but before the cookie is
  // actually revoked (e.g. apiClient's refresh-on-401 retry, triggered by some other
  // request that raced the token clear). Without this flag, that new refresh call hits
  // /refresh, finds the cookie still valid, and silently logs the user back in right
  // after they clicked "Log out."
  let signOutInFlight = false;

  const useAuthStore = create<AuthStore<TUser, TCredentials>>((set, get) => {
    const scheduleRefresh = (expiresInMinutes: number, accessToken?: string) => {
      if (refreshTimer) clearTimeout(refreshTimer);
      const margin = config.refreshMarginMs ?? DEFAULT_REFRESH_MARGIN_MS;

      // Expiry sources, most to least authoritative: an explicit lifetime in the
      // refresh response (admin portal sends `expires_in_minutes`), then the JWT's
      // own `exp` claim (patient portal sends no lifetime field, but the token
      // always carries one), then the blind fallback.
      const fromResponse = Number.isFinite(expiresInMinutes) && expiresInMinutes > 0 ? expiresInMinutes : NaN;
      const fromJwt = readJwtExpiryMinutes(accessToken);
      const jwtUsable = Number.isFinite(fromJwt) && fromJwt > 0;
      const minutes = Number.isFinite(fromResponse)
        ? fromResponse
        : jwtUsable
          ? fromJwt
          : FALLBACK_EXPIRES_IN_MINUTES;

      if (import.meta.env.DEV && !Number.isFinite(fromResponse) && !jwtUsable) {
        console.warn(
          `[auth:${config.portal}] no usable token expiry (response field and JWT exp both unreadable); falling back to ${FALLBACK_EXPIRES_IN_MINUTES}m.`,
        );
      }

      const delay = Math.max(minutes * 60_000 - margin, 5_000);

      if (import.meta.env.DEV) {
        console.debug(`[auth:${config.portal}] next proactive refresh in ${Math.round(delay / 1000)}s`);
      }

      refreshTimer = setTimeout(() => {
        void refreshSilently();
      }, delay);
    };

    const setSession = (data: AuthSession<TUser>) => {
      generation++;
      set({ accessToken: data.accessToken, user: data.user, isAuthenticated: true });
      scheduleRefresh(data.expiresIn, data.accessToken);
    };

    const clearSession = () => {
      generation++;
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = undefined;
      set({ accessToken: null, user: null, isAuthenticated: false });
    };

    // Shared by silentRestore (on mount), the proactive refresh timer, and apiClient's
    // refresh-on-401 binding. All three can fire around the same time; refreshPromise
    // ensures they share a single network call instead of racing separate ones.
    const refreshSilently = (): Promise<boolean> => {
      if (signOutInFlight) return Promise.resolve(false);
      if (refreshPromise) return refreshPromise;
      const startedAtGeneration = generation;

      refreshPromise = (async () => {
        const { data } = await config.refresh();
        if (generation !== startedAtGeneration) {
          // Superseded by a sign-out (or another session change) while this call was
          // in flight — applying it now would resurrect a session that was just cleared.
          return false;
        }
        if (!data) {
          clearSession();
          return false;
        }
        setSession(data);
        return true;
      })().finally(() => {
        refreshPromise = null;
      });

      return refreshPromise;
    };

    registerAuthBinding(config.portal, {
      getToken: () => get().accessToken,
      refresh: refreshSilently,
      clear: clearSession,
    });

    // A backgrounded tab's setTimeout is throttled or paused, so the proactive
    // refresh timer can fire long after the token has already expired — the next
    // request then eats a 401 (recovered by apiClient's refresh-on-401 retry, but
    // it still shows up in the console and adds a round trip). When the tab comes
    // back to the foreground, pre-empt that: if the current token is already
    // within the refresh margin of expiry, refresh now. refreshSilently dedupes,
    // so this is safe alongside the timer and any in-flight 401 retry.
    if (typeof document !== "undefined") {
      const refreshIfExpiringSoon = () => {
        if (document.visibilityState !== "visible") return;
        const minutesLeft = readJwtExpiryMinutes(get().accessToken);
        if (!Number.isFinite(minutesLeft)) return; // no token, or unreadable exp — leave it to the timer
        const marginMinutes = (config.refreshMarginMs ?? DEFAULT_REFRESH_MARGIN_MS) / 60_000;
        if (minutesLeft <= marginMinutes) void refreshSilently();
      };
      document.addEventListener("visibilitychange", refreshIfExpiringSoon);
      window.addEventListener("focus", refreshIfExpiringSoon);
    }

    return {
      accessToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setSession,
      clearSession,

      signIn: async (...credentials) => {
        const { data, error, code, retryAfterSeconds, otpExpiresInSeconds } = await config.signIn(...credentials);
        if (!data) {
          return {
            success: false,
            error: error ?? undefined,
            code: code ?? undefined,
            retryAfterSeconds,
            otpExpiresInSeconds,
          };
        }
        setSession(data);
        return { success: true };
      },

      signOut: async () => {
        // Clear local state first — the server-side revoke call can take a while (observed
        // ~5s round trip), and callers like AdminSidebar navigate away immediately without
        // awaiting this promise. If clearSession ran only after the network call, the app
        // would briefly navigate to the sign-in route while isAuthenticated was still true,
        // get bounced straight back by the guest-only guard, and only actually log out once
        // the slow request finally resolved — the "first click does nothing" symptom.
        clearSession();
        // Block any refresh attempt (e.g. apiClient's refresh-on-401 retry, triggered by
        // some unrelated request that raced the token clear above) until the cookie is
        // actually revoked — otherwise that refresh can succeed against the still-valid
        // cookie and silently re-authenticate the user right after they logged out.
        signOutInFlight = true;
        try {
          await config.signOut().catch(() => {});
        } finally {
          signOutInFlight = false;
        }
      },

      silentRestore: async () => {
        set({ isLoading: true });
        await refreshSilently();
        set({ isLoading: false });
      },
    };
  });

  return useAuthStore;
}
